import {
  Button,
  ErrorBoundary,
  Icon,
  SectionContainer,
  Text,
  HStack,
  VStack,
} from '@genial/components';
import { Color, Spacing, Typography } from '@genial/design-tokens';
import { Service } from '@genial/services';
import { useNavigation } from '@react-navigation/native';
import { Box, Center } from 'native-base';
import React, { useEffect, useMemo, useState } from 'react';
import { ScrollView } from 'react-native-gesture-handler';
import { useRecoilState, useRecoilValue } from 'recoil';

import { useAbsoluteRoute } from '../../../../hooks/useAbsoluteRoute';
import { useRecoilQuery } from '../../../../hooks/useRecoilQuery';
import { SignedInNavigationScreenNavigationProp } from '../../../../routes/types';
import { fundraisingSummaryQuery } from '../../../../store/home/capture/selector';
import { FundraisingSummaryResponseNormalized } from '../../../../store/home/capture/types';
import { financialSummuryOrdenationState } from '../../../../store/home/financialSummuryOrdenation/atom';
import {
  advisorIdToApisSelector,
  partnerIdToApisSelector,
} from '../../../../store/master/selector';
import { FeatureToggle, useUnleashFlags } from '../../../../utils/useUnleashFlags';

import { CheckingAccountItem } from './components/CheckingAccountItem';
import { FinancialTable } from './components/FinancialTable/FinancialTable';
import { FutureMaturitiesItem } from './components/FutureMaturitiesItem';
import { PendingRedemptionItem } from './components/PendingRedemptionItem';
import {
  columnWidthsCheckingAccount,
  columnWidthsPendingRedemption,
  columnWidthsFutureMaturities,
  buttons,
  summaryOptions,
  tableHeaders,
} from './constants';
import { useFinancialSummaryOrdenation } from './hooks/financialSummaryOrdenation';

export namespace FinancialSummaryTypes {
  export interface Props {
    isMobile?: boolean;
  }
}

export const FinancialSummary: React.FC<FinancialSummaryTypes.Props> = ({ isMobile }) => {
  const [allData, setAllData] = useState<FundraisingSummaryResponseNormalized[]>([]);
  const [selectedViewOption, setSelectedViewOption] = useState('CurrentAccount');
  const [isLoading, setIsLoading] = useState(false);
  const [activeButton, setActiveButton] = useState('Conta corrente');
  const { b2bShowLegacyCostumerPosition } = useUnleashFlags(FeatureToggle.legacyCostumerPosition);

  const [{ selectedDisplayOrder, selectedOrderType, hasOrdernation }] = useRecoilState(
    financialSummuryOrdenationState
  );
  const navigation = useNavigation<SignedInNavigationScreenNavigationProp>();
  const currentRoute = useAbsoluteRoute();
  const selectedPartner = useRecoilValue(partnerIdToApisSelector);
  const selectedAdvisor = useRecoilValue(advisorIdToApisSelector);

  const {
    data: financialSummary,
    loading: financialSummaryIsLoading,
    error: financialSummaryIsError,
    refresher,
  } = useRecoilQuery(fundraisingSummaryQuery({ summaryType: selectedViewOption }));

  const renderers = useMemo(
    () => ({
      CurrentAccount: (item) => (
        <CheckingAccountItem item={item} onSelectAccount={handleSelectAccount} />
      ),

      WithdrawnToBeSettled: (item) => <PendingRedemptionItem item={item} />,

      FutureMaturities: (item) => (
        <FutureMaturitiesItem item={item} onSelectAccount={handleSelectAccount} />
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedPartner, selectedAdvisor]
  );

  useEffect(() => {
    setIsLoading(financialSummaryIsLoading);
  }, [financialSummaryIsLoading]);

  const content = useMemo(() => {
    if (financialSummaryIsError) {
      return <ErrorBoundary onRefresh={refresher} />;
    }

    if (!isLoading && allData.length <= 0) {
      return (
        <Center h={'400px'}>
          <ErrorBoundary
            errorIconOrIllustration="NotFoundIllustration"
            errorTitle="Nenhum resultado corresponde à sua pesquisa"
            errorMessage="Não há resultados correspondentes a este período de 30 dias"
          />
        </Center>
      );
    }

    const columnWidths = {
      CurrentAccount: columnWidthsCheckingAccount,
      WithdrawnToBeSettled: columnWidthsPendingRedemption,
      FutureMaturities: columnWidthsFutureMaturities,
    };

    return (
      <FinancialTable
        isLoading={isLoading}
        allData={allData as any}
        isMobile={isMobile}
        columnWidths={columnWidths[selectedViewOption]}
        renderItem={({ item }) => renderers[selectedViewOption](item)}
        headers={tableHeaders[selectedViewOption]}
      />
    );
  }, [
    financialSummaryIsError,
    isLoading,
    allData,
    isMobile,
    selectedViewOption,
    refresher,
    renderers,
  ]);

  const contentPeriod = useMemo(
    () => (
      <HStack
        paddingBottom={Spacing['stack.xxs']}
        justifyContent="space-between"
        alignItems="center">
        <HStack space={Spacing['inline.quarck']}>
          <Icon name="Calendar" testId="calendar" size="xxxs" color={Color['neutral.medium']} />
          <Text color="neutral.medium" fontSize={Typography['fontsize.md']}>
            Período:
          </Text>
          <Text variant="semibold">
            {selectedViewOption === 'CurrentAccount' ? 'ÚLTIMOS 30 DIAS' : 'PRÓXIMOS 30 DIAS'}
          </Text>
        </HStack>
        {isMobile && (
          <Button
            maxW={'52px'}
            variant="secondary"
            leftIcon={<Icon testId="filter-icon" name={'Ordering'} size="xxxs" />}
            onPress={() => {
              navigation.navigate('FinancialSummaryFilter', {
                ordernationTypes: tableHeaders[selectedViewOption],
              });
            }}>
            <HStack alignItems="center" justifyContent="center" space={Spacing['inline.nano']}>
              {hasOrdernation && (
                <Box
                  position="absolute"
                  right={-20}
                  top={-20}
                  rounded={'full'}
                  w={'14px'}
                  h={'14px'}
                  alignItems={'center'}
                  justifyContent={'center'}
                  bg={'brand.tertiary.light'}>
                  <Text
                    variant="regular"
                    fontSize={Typography['fontsize.xs']}
                    color={'neutral.white'}
                  />
                </Box>
              )}
            </HStack>
          </Button>
        )}
      </HStack>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedViewOption, hasOrdernation, isMobile]
  );

  const handleSelectAccount = async (accountNumber: number) => {
    setIsLoading(true);

    if (!accountNumber) {
      return;
    }

    const { data: customers } = await Service.Customer.V1.getByAccountNumber({
      accountNumber,
      partnerId: selectedPartner,
      advisorId: selectedAdvisor,
      screenName: 'anywhere',
    });

    if (!customers) {
      setIsLoading(false);
      return;
    }

    const { customer } = customers;

    if (b2bShowLegacyCostumerPosition) {
      const method =
        currentRoute.name === 'AngularFrame_CustomerDetail'
          ? navigation.replace
          : navigation.navigate;
      method('AppDrawer', {
        screen: 'AngularFrame_CustomerDetail',
        params: {
          state: 'app.posicaoOnline.posicao',
          DocumentoCliente: customer.cpfCnpjText,
          IdConta: customer.accountId,
        },
      });
    } else {
      navigation.navigate('AppDrawer', {
        screen: 'CustomerDetail',
        params: {
          customerId: customer.customerId,
          accountNumber: customer.accountNumber,
          accountId: customer.accountId,
          cpfCnpj: customer.cpfCnpjText,
        },
      });
    }
  };

  const onViewChange = (value: string) => {
    setAllData([]);
    setActiveButton(value);
    setSelectedViewOption(value);
  };

  const onSelectPeriodButton = (label: string, value: string) => {
    setAllData([]);
    setActiveButton(label);
    setSelectedViewOption(value);
  };

  const sortedData = useFinancialSummaryOrdenation(financialSummary ?? [], {
    key: selectedOrderType,
    direction: selectedDisplayOrder,
  });

  useEffect(() => {
    if (!isLoading) {
      setAllData(sortedData);
    }
  }, [isLoading, sortedData]);

  if (isMobile) {
    return (
      <Box width={{ base: 'full', lg: '3/5' }} paddingX={Spacing['stack.xxxs']}>
        <VStack paddingBottom={Spacing['stack.xxs']} space={Spacing['stack.xxxs']}>
          <ScrollView horizontal persistentScrollbar={false} showsHorizontalScrollIndicator={false}>
            <HStack space={Spacing['stack.nano']} minWidth="max-content" flex={1}>
              {buttons.map((button) => (
                <Button
                  key={button.key}
                  maxWidth={140}
                  isDisabled={isLoading}
                  variant={activeButton === button.label ? 'tertiary' : 'secondary'}
                  bg={
                    activeButton === button.label ? 'brand.primary.principal' : 'neutral.lightest'
                  }
                  onPress={() => onSelectPeriodButton(button.label, button.key)}
                  _text={{
                    textTransform: 'initial',
                    color: activeButton === button.label ? 'neutral.white' : 'neutral.medium',
                  }}>
                  {button.label}
                </Button>
              ))}
            </HStack>
          </ScrollView>
        </VStack>
        {contentPeriod}
        {content}
      </Box>
    );
  }

  return (
    <Box width={{ base: 'full', lg: '3/5' }}>
      <SectionContainer
        flex={1}
        title="RESUMO FINANCEIRO"
        withFilter
        withSeparator
        multipleFilterLabel="Selecione uma opção:"
        filterOptions={summaryOptions}
        selectedOption={selectedViewOption}
        onFilterChange={onViewChange}>
        {contentPeriod}
        {content}
      </SectionContainer>
    </Box>
  );
};
